import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';

const locationFaqs = [
  {
    question: 'Why should I choose BitNinja Technologies in Surat for software development?',
    answer: 'BitNinja Technologies is one of Surat\'s fastest-growing IT companies, offering world-class software development at competitive Indian pricing. With our headquarters in the Meridian Business Center, we provide direct face-to-face collaboration, rapid turnaround times, and deep understanding of both local and global market needs. Our Surat team handles everything from custom web apps to enterprise cloud solutions.',
  },
  {
    question: 'What types of software projects does BitNinja handle from Surat?',
    answer: 'Our Surat office handles the full spectrum: custom web applications, mobile apps (iOS & Android), SaaS platforms, e-commerce solutions, ERP systems, cloud infrastructure, UI/UX design, and digital marketing. We work with startups building MVPs, mid-size companies scaling their tech, and enterprises needing ongoing development support.',
  },
  {
    question: 'How quickly can I get a software project quote from BitNinja Surat?',
    answer: 'Fill out the inquiry form on this page and our Surat team typically responds within 30 minutes during business hours (Mon-Sat, 10 AM - 7 PM IST). We will schedule a free 30-minute consultation to understand your requirements and provide a detailed quote within 24 hours.',
  },
  {
    question: 'Does BitNinja Surat work with international clients?',
    answer: 'Absolutely. Our Surat headquarters serves clients across North America, Europe, the Middle East, and Asia-Pacific. We operate on flexible time zones, use tools like Slack, Zoom, and Jira for seamless communication, and have extensive experience with remote collaboration — distance is never a barrier.',
  },
  {
    question: 'What technologies does the Surat development team specialize in?',
    answer: 'Our Surat team excels in React, Next.js, TypeScript, Node.js, Python, PostgreSQL, MongoDB, AWS, Docker, React Native, and Flutter. We follow agile methodology with daily standups, sprint planning, and regular demos so you always know exactly where your project stands.',
  },
  {
    question: 'Can I visit the BitNinja Surat office for a consultation?',
    answer: 'Yes, we welcome in-person consultations at our Surat headquarters: 212, Meridian Business Center, Lajamni Chowk, Mota Varachha, Surat, Gujarat 394101. Simply book a meeting through our contact form or call +91 9033779369 and we will schedule a time that works for you.',
  },
  {
    question: 'What industries does BitNinja Surat serve?',
    answer: 'We have built software for fintech, healthcare, real estate, e-commerce, logistics, education, hospitality, and manufacturing industries. Our diverse portfolio across sectors means we bring cross-industry best practices to every project, regardless of your domain.',
  },
];

const locationJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "BitNinja Technologies — Surat",
    "image": "https://storage.readdy-site.link/project_files/932d0adb-bf56-44ff-a11c-0aa982709dd7/0499b76b-9ae9-4384-9aac-227cd2d8a42c_compressed_BITNINJA-final-image.webp",
    "url": "https://bitninjatech.com/surat",
    "telephone": "+91 9033779369",
    "email": "info@bitninjatech.com",
    "description": "BitNinja Technologies Surat headquarters — custom software development, web apps, mobile apps, cloud solutions, and IT consulting for businesses in Surat, Gujarat, and worldwide.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "212, Meridian Business Center, Lajamni Chowk, Mota Varachha",
      "addressLocality": "Surat",
      "addressRegion": "Gujarat",
      "postalCode": "394101",
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 21.2358,
      "longitude": 72.8777
    },
    "openingHoursSpecification": [
      { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"], "opens": "10:00", "closes": "19:00" }
    ],
    "areaServed": ["Surat", "Gujarat", "India", "Worldwide"],
    "priceRange": "$$"
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://bitninjatech.com" },
      { "@type": "ListItem", "position": 2, "name": "Surat Office", "item": "https://bitninjatech.com/surat" }
    ]
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": locationFaqs.map((item) => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer }
    }))
  }
];

const LocationSuratPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', company: '', service: '', message: '' });
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'message') setCharCount(value.length);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SeoHead
        title="IT Company Surat | Software Development & IT Services | BitNinja Technologies"
        description="BitNinja Technologies is a top-rated IT company in Surat, Gujarat offering custom software development, web apps, mobile apps, cloud solutions, and IT consulting. Get a free quote today."
        canonicalUrl="https://bitninjatech.com/surat"
        jsonLd={locationJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 md:pt-32 overflow-hidden bg-foreground-900 min-h-[520px] md:min-h-[560px] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20tech%20office%20interior%20in%20Surat%20India%20with%20warm%20ambient%20lighting%20clean%20white%20walls%20wooden%20accents%20computer%20screens%20displaying%20code%20subtle%20indian%20design%20elements%20professional%20workspace%20atmosphere%20soft%20shadows%20editorial%20photography%20style%20high%20detail&width=1600&height=900&seq=surat-hero-2026&orientation=landscape"
            alt="BitNinja Technologies Surat Office"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
        </div>
        <div className="section-padding relative z-10 w-full py-16">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-background-300 mb-6">
              <Link to="/" className="hover:text-primary-500 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-primary-400">Surat Office</span>
            </div>
            <div className="inline-flex items-center gap-2 bg-primary-500/20 border border-primary-500/30 rounded-full px-4 py-1.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-primary-400 animate-pulse"></span>
              <span className="text-xs font-mono uppercase tracking-widest text-primary-300">Headquarters</span>
            </div>
            <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-bold leading-[1.02] tracking-tight text-background-50 mb-4 max-w-4xl">
              Software Development Company in{' '}
              <span className="text-primary-400">Surat, Gujarat</span>
            </h1>
            <p className="text-background-200 text-lg md:text-xl mb-8 max-w-2xl">
              Custom web apps, mobile apps, and enterprise software — built by Surat&apos;s sharpest engineering team. Your ideas, engineered to scale.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#contact-form" className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3.5 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap">
                Get a Free Quote
                <i className="ri-arrow-right-up-line text-base"></i>
              </a>
              <a href="#services-overview" className="inline-flex items-center gap-2 bg-background-50/10 backdrop-blur-sm hover:bg-background-50/20 text-background-50 px-6 py-3.5 rounded-full text-sm font-semibold border border-background-50/20 transition-colors cursor-pointer whitespace-nowrap">
                Our Services
                <i className="ri-arrow-down-line"></i>
              </a>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-background-300">
              <div className="flex items-center gap-2">
                <i className="ri-map-pin-line text-primary-400"></i>
                <span>Lajamni Chowk, Mota Varachha, Surat 394101</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-phone-line text-primary-400"></i>
                <span>+91 9033779369</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="bg-primary-500 border-t border-primary-400/20">
        <div className="section-padding py-8">
          <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: '50+', label: 'Projects Delivered' },
              { value: '20+', label: 'Team Engineers' },
              { value: '98%', label: 'Client Satisfaction' },
              { value: '30 min', label: 'Avg. Response Time' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="font-display text-3xl md:text-4xl font-bold text-background-50 mb-1">{stat.value}</div>
                <div className="text-xs md:text-sm text-background-50/70 uppercase tracking-wider font-mono">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Surat Presence */}
      <section className="py-14 md:py-16 bg-background-50 border-t border-background-200">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-center gap-10 lg:gap-16">
              <div className="lg:w-1/2">
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex w-6 h-6 items-center justify-center bg-foreground-900 text-background-50 rounded-sm">
                    <i className="ri-building-line text-xs"></i>
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-foreground-600">Headquarters</span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-bold leading-[1.02] tracking-tight text-foreground-900 mb-6">
                  Surat&apos;s Premier{' '}
                  <span className="text-primary-500">Software Company.</span>
                </h2>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  BitNinja Technologies was founded in Surat with a single mission: bring world-class software engineering to businesses in Gujarat and beyond. From our headquarters in the Meridian Business Center at Lajamni Chowk, we have grown into a 20+ person engineering team serving clients across India, the US, Europe, and the Middle East.
                </p>
                <p className="text-foreground-700 leading-relaxed mb-4">
                  Our Surat office is the heartbeat of BitNinja — where our core engineering, design, and project management teams collaborate daily. We invest heavily in local talent, running regular tech workshops and hiring from Surat&apos;s top engineering colleges.
                </p>
                <p className="text-foreground-700 leading-relaxed">
                  Whether you&apos;re a Surat-based startup needing an MVP or an international enterprise looking for a reliable Indian development partner, our Surat team delivers quality code, on time, at competitive rates.
                </p>
              </div>
              <div className="lg:w-1/2">
                <div className="rounded-2xl overflow-hidden border border-background-200 h-80 lg:h-96">
                  <img
                    src="https://readdy.ai/api/search-image?query=Bright%20modern%20tech%20office%20workspace%20in%20Surat%20India%20with%20open%20floor%20plan%20programmers%20at%20desks%20large%20monitors%20white%20walls%20warm%20wood%20furniture%20indoor%20plants%20natural%20daylight%20through%20large%20windows%20clean%20minimalist%20aesthetic%20professional%20collaborative%20atmosphere%20high%20detail%20editorial%20style&width=1200&height=800&seq=surat-office-interior&orientation=landscape"
                    alt="BitNinja Surat office interior"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section id="services-overview" className="py-14 md:py-16 bg-background-100 border-t border-background-200">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="inline-flex w-6 h-6 items-center justify-center bg-foreground-900 text-background-50 rounded-sm">
                  <i className="ri-code-line text-xs"></i>
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-foreground-600">What We Build in Surat</span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold leading-[1.02] tracking-tight text-foreground-900">
                Custom Software.{' '}
                <span className="text-primary-500">Any Scale.</span>
              </h2>
              <p className="mt-4 text-foreground-600 max-w-2xl mx-auto text-[15px] leading-relaxed">
                From startup MVPs to enterprise platforms — our Surat engineering team handles the full development lifecycle.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { icon: 'ri-global-line', title: 'Web Application Development', desc: 'React, Next.js, Node.js — fast, responsive, SEO-optimized web apps built for scale. Custom dashboards, SaaS platforms, and corporate portals.' },
                { icon: 'ri-smartphone-line', title: 'Mobile App Development', desc: 'Native iOS (Swift) and Android (Kotlin), plus cross-platform React Native and Flutter apps. From concept to App Store deployment.' },
                { icon: 'ri-cloud-line', title: 'Cloud & DevOps', desc: 'AWS, Azure, and GCP infrastructure. Serverless architectures, CI/CD pipelines, Docker, Kubernetes — your app running reliably at any scale.' },
                { icon: 'ri-database-2-line', title: 'Backend & API Development', desc: 'RESTful and GraphQL APIs with Node.js, Python, or Go. Database design with PostgreSQL, MongoDB, or MySQL. Microservices architecture.' },
                { icon: 'ri-palette-line', title: 'UI/UX Design', desc: 'End-to-end design in Figma — user research, wireframes, interactive prototypes, and complete design systems. Beautiful interfaces that convert.' },
                { icon: 'ri-shopping-cart-2-line', title: 'E-Commerce Solutions', desc: 'Custom Shopify stores, headless commerce with Shopify Storefront API, payment integration with Stripe. Full-featured online stores.' },
              ].map((s, i) => (
                <div key={i} className="bg-background-50 rounded-2xl p-6 border border-background-200 hover:-translate-y-1 transition-all duration-300 group">
                  <div className="w-12 h-12 rounded-xl bg-foreground-900 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <i className={`${s.icon} text-xl text-background-50`}></i>
                  </div>
                  <h3 className="font-display font-bold text-foreground-900 mb-2 text-sm">{s.title}</h3>
                  <p className="text-foreground-600 text-xs leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-14 md:py-16 bg-foreground-900 border-t border-foreground-800">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="inline-flex w-6 h-6 items-center justify-center bg-primary-500 text-background-50 rounded-sm">
                  <i className="ri-star-line text-xs"></i>
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-background-400">Why Surat</span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-background-50">
                Why Businesses Choose{' '}
                <span className="text-primary-500">BitNinja Surat.</span>
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: 'ri-money-rupee-circle-line', title: 'Competitive Pricing', desc: 'World-class code at Indian rates. Get enterprise-grade software without the Silicon Valley price tag — same quality, better value.' },
                { icon: 'ri-time-line', title: 'Rapid Turnaround', desc: 'Agile sprints with weekly deliverables. Our Surat team ships features fast without cutting corners on quality or testing.' },
                { icon: 'ri-user-voice-line', title: 'Direct Communication', desc: 'Talk directly with the engineers building your product. No account managers filtering the conversation — just real, technical discussions.' },
                { icon: 'ri-global-line', title: 'Timezone Advantage', desc: 'IST overlaps with EU mornings and US afternoons. We schedule calls when it works for you, not just when it works for us.' },
              ].map((item, i) => (
                <div key={i} className="bg-background-50/5 border border-background-50/10 rounded-2xl p-6 hover:border-primary-500/40 transition-all">
                  <div className="w-12 h-12 rounded-xl bg-primary-500 flex items-center justify-center mb-4">
                    <i className={`${item.icon} text-xl text-background-50`}></i>
                  </div>
                  <h3 className="font-display font-bold text-background-50 mb-2 text-sm">{item.title}</h3>
                  <p className="text-background-400 text-xs leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-14 md:py-16 bg-background-100 border-t border-background-200">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground-900">
                Tech Stack.{' '}
                <span className="text-primary-500">Modern & Battle-Tested.</span>
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {['React', 'Next.js', 'TypeScript', 'Node.js', 'Python', 'PostgreSQL', 'MongoDB', 'AWS', 'Docker', 'React Native', 'Flutter', 'GraphQL'].map((tech, i) => (
                <div key={i} className="bg-background-50 border border-background-200 rounded-xl px-5 py-4 text-center hover:border-foreground-900/30 transition-all cursor-default">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-foreground-700">{tech}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-16 bg-background-50 border-t border-background-200">
        <div className="section-padding">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="inline-flex w-6 h-6 items-center justify-center bg-foreground-900 text-background-50 rounded-sm">
                  <i className="ri-question-answer-line text-xs"></i>
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-foreground-600">FAQ</span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground-900">
                Surat Software Development{' '}
                <span className="text-primary-500">FAQs.</span>
              </h2>
            </div>
            <div className="space-y-3">
              {locationFaqs.map((item, i) => (
                <details key={i} className="group bg-background-100 rounded-2xl border border-background-200 overflow-hidden transition-all duration-300">
                  <summary className="flex items-center justify-between p-5 md:p-6 cursor-pointer list-none">
                    <h3 className="font-display text-base md:text-lg font-bold text-foreground-900 pr-4 group-open:text-primary-500 transition-colors">{item.question}</h3>
                    <span className="w-8 h-8 flex items-center justify-center rounded-full bg-background-50 border border-background-200 flex-shrink-0 group-open:bg-foreground-900 group-open:border-foreground-900 transition-all">
                      <i className="ri-add-line text-foreground-700 group-open:text-background-50 group-open:hidden"></i>
                      <i className="ri-subtract-line text-background-50 hidden group-open:block"></i>
                    </span>
                  </summary>
                  <div className="px-5 md:px-6 pb-5 md:pb-6">
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form + Map */}
      <section id="contact-form" className="py-14 md:py-16 bg-foreground-900 border-t border-foreground-800">
        <div className="section-padding">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10 md:mb-14">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="inline-flex w-6 h-6 items-center justify-center bg-primary-500 text-background-50 rounded-sm">
                  <i className="ri-send-plane-line text-xs"></i>
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-background-400">Start Your Project</span>
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-background-50">
                Let&apos;s Build Something{' '}
                <span className="text-primary-500">Great.</span>
              </h2>
              <p className="mt-4 text-background-300 max-w-xl mx-auto text-[15px]">
                Tell us about your software project. An engineer from our Surat team — not a sales rep — will get back to you within 30 minutes.
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
              <div className="lg:col-span-7 bg-background-50 rounded-2xl p-7 md:p-9">
                <form
                  data-readdy-form
                  action="https://readdy.ai/api/form/d9168tgbhec1he3oppbg"
                  method="POST"
                >
                  <input tabIndex={-1} autoComplete="off" aria-hidden="true" className="nc-decoy-field" name="contact_alt" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block font-mono text-[10px] uppercase tracking-widest text-foreground-500 mb-2">Full Name</label>
                      <input id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full text-sm bg-background-100 border border-background-200 rounded-md px-4 py-3 outline-none focus:border-foreground-900 transition-colors" placeholder="Priya Patel" />
                    </div>
                    <div>
                      <label htmlFor="email" className="block font-mono text-[10px] uppercase tracking-widest text-foreground-500 mb-2">Work Email</label>
                      <input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} className="w-full text-sm bg-background-100 border border-background-200 rounded-md px-4 py-3 outline-none focus:border-foreground-900 transition-colors" placeholder="priya@company.com" />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block font-mono text-[10px] uppercase tracking-widest text-foreground-500 mb-2">Phone <span className="text-foreground-400 normal-case">(optional)</span></label>
                      <input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} className="w-full text-sm bg-background-100 border border-background-200 rounded-md px-4 py-3 outline-none focus:border-foreground-900 transition-colors" placeholder="+91 98765 43210" />
                    </div>
                    <div>
                      <label htmlFor="company" className="block font-mono text-[10px] uppercase tracking-widest text-foreground-500 mb-2">Company <span className="text-foreground-400 normal-case">(optional)</span></label>
                      <input id="company" name="company" value={formData.company} onChange={handleChange} className="w-full text-sm bg-background-100 border border-background-200 rounded-md px-4 py-3 outline-none focus:border-foreground-900 transition-colors" placeholder="Your Company" />
                    </div>
                    <div className="md:col-span-2">
                      <label htmlFor="service" className="block font-mono text-[10px] uppercase tracking-widest text-foreground-500 mb-2">Service Interested In</label>
                      <select id="service" name="service" value={formData.service} onChange={handleChange} className="w-full text-sm bg-background-100 border border-background-200 rounded-md px-4 py-3 outline-none focus:border-foreground-900 transition-colors">
                        <option value="">Select a service</option>
                        <option value="Web Application Development">Web Application Development</option>
                        <option value="Mobile App Development">Mobile App Development</option>
                        <option value="Cloud & DevOps">Cloud &amp; DevOps</option>
                        <option value="Backend & API Development">Backend &amp; API Development</option>
                        <option value="UI/UX Design">UI/UX Design</option>
                        <option value="E-Commerce Solutions">E-Commerce Solutions</option>
                        <option value="IT Consulting">IT Consulting</option>
                      </select>
                    </div>
                  </div>
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-2">
                      <label htmlFor="message" className="block font-mono text-[10px] uppercase tracking-widest text-foreground-500">Your Message</label>
                      <span className="font-mono text-[10px] text-foreground-500">{charCount} / 500</span>
                    </div>
                    <textarea id="message" name="message" rows={5} maxLength={500} required value={formData.message} onChange={handleChange} className="w-full text-sm bg-background-100 border border-background-200 rounded-md px-4 py-3 outline-none focus:border-foreground-900 transition-colors resize-none" placeholder="Tell us about your project, goals, timeline, budget range — anything that helps us understand what you need..."></textarea>
                  </div>
                  <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <p className="text-xs text-foreground-500 max-w-sm">By submitting, you agree to BitNinja Surat contacting you about your inquiry. We never sell or share your details.</p>
                    <button type="submit" className="inline-flex items-center justify-center gap-2 bg-foreground-900 hover:bg-foreground-800 text-background-50 text-sm font-semibold px-6 py-3.5 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                      Send Inquiry
                      <i className="ri-arrow-right-up-line text-base"></i>
                    </button>
                  </div>
                </form>
              </div>
              <div className="lg:col-span-5 flex flex-col gap-6">
                <div className="bg-background-50/[0.04] border border-background-50/15 rounded-2xl p-6 space-y-4">
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 flex-shrink-0"><i className="ri-map-pin-line text-base"></i></span>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-background-400 mb-1">Visit Us</div>
                      <div className="text-sm text-background-50">212, Meridian Business Center, Lajamni Chowk, Mota Varachha,<br />Surat, Gujarat 394101</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 flex-shrink-0"><i className="ri-phone-line text-base"></i></span>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-background-400 mb-1">Call Us</div>
                      <div className="text-sm text-background-50">+91 9033779369</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 flex-shrink-0"><i className="ri-mail-line text-base"></i></span>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-background-400 mb-1">Email</div>
                      <div className="text-sm text-background-50">info@bitninjatech.com</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 flex-shrink-0"><i className="ri-time-line text-base"></i></span>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-background-400 mb-1">Hours</div>
                      <div className="text-sm text-background-50">Mon–Sat, 10:00 AM – 7:00 PM IST</div>
                    </div>
                  </div>
                </div>
                <div className="rounded-2xl overflow-hidden border border-background-50/15 h-56">
                  <iframe
                    title="BitNinja Technologies Surat Office"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3719.7!2d72.8777!3d21.2358!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be04f0e0b000001%3A0x1!2sMeridian+Business+Center%2C+Lajamni+Chowk%2C+Mota+Varachha%2C+Surat%2C+Gujarat+394101!5e0!3m2!1sen!2sin!4v1712000000000!5m2!1sen!2sin"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LocationSuratPage;